import React, { useState, useEffect } from "react";
import {
  FormGroup,
  Input,
  CustomInput,
  FormFeedback,
  Button,
} from "reactstrap";
import { Formik, Form, Field } from "formik";
import * as yup from "yup";
import axios from "axios";
import { useHistory, withRouter } from "react-router-dom";
import { useDispatch } from "react-redux";
import AuthLayout from "../Authentication";
import { signUp } from "../../../Store/actions/AuthAction";
import { API_URL } from "../../../Constants/endPoints";
import { captureEvent, logger } from "../../../Utils";
import { Helmet } from "react-helmet";
import { ModalBox } from "../../../App/components";
import TermsAndConditionContent from "../../TermsAndConditions/TermsAndConditionContent";

const schema = yup.object().shape({
  email: yup.string().required().email("Enter valid email address"),
  firstName: yup.string()
    .matches(/^[^\s][A-Za-z0-9\s]*[^\s]$/, "Invalid characters")
    .required('First name is required'),
  lastName: yup.string()
    .matches(/^[^\s][A-Za-z0-9\s]*[^\s]$/, "Invalid characters")
    .required('Last name is required'),
  phone: yup.string()
    .matches(/^[0-9]{10}$/, "Must be exactly 10 digits")
    .required('Phone number is required'),
  password: yup.string().required('Password is required'),
  repeat_password: yup.string()
    .oneOf([yup.ref("password"), null], "Passwords do not match")
    .required("Confirm password is required"),
});

const SignUp = (props) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [duplicateEmail, setDuplicateEmail] = useState(false);
  const [duplicatePhone, setDuplicatePhone] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [componentLoad, setComponentLoad] = useState('tenant');
  const [showPass, setShowPass] = useState(false);
  const [showRepeatPass, setShowRepeatPass] = useState(false);
  const [termsAndService, setTermsAndService] = useState(false);
  const [importantUpdate, setImportantUpdate] = useState(false);
  const [modalTermsAndService, setModalTermsAndService] = useState(false);
  const log = logger("SignUp");

  useEffect(() => {
    setComponentLoad(props.match.params.slug);
    window.scrollTo(0, 0);
  }, [props.match.params.slug]);

  const togglePassword = (type) => {
    type === 'pass' ? setShowPass(!showPass) : setShowRepeatPass(!showRepeatPass);
  };

  const renderInput = ({ type, id, placeholder, emailError, phoneError, maxLength }) => {
    return (
      <FormGroup>
        <Field name={id}>
          {({ field: { name, value, onChange, onBlur }, form: { touched, errors }, meta }) => {
            let hasError = !!meta.touched && !!meta.error;
            let errorMessage = meta.error;
            if (name === "email" && duplicateEmail) {
              hasError = true;
              errorMessage = duplicateEmail;
            }
            if (name === "phone" && duplicatePhone) {
              hasError = true;
              errorMessage = duplicatePhone;
            }
            return (
              <div style={{ position: 'relative' }}>
                <Input
                  type={(type === 'password') ?
                    (name === 'password') ? (showPass ? 'text' : 'password') :
                      (name === 'repeat_password') && (showRepeatPass ? 'text' : 'password') : type}
                  name={name}
                  id={id}
                  invalid={hasError}
                  placeholder={placeholder}
                  onChange={onChange}
                  onBlur={onBlur}
                  value={value}
                  maxLength={maxLength}
                />
                {type === 'password' && (
                  <p style={{
                    position: 'absolute',
                    top: '17px',
                    right: '11px',
                    fontSize: '11px',
                    fontWeight: '600',
                    opacity: '0.5',
                    cursor: 'pointer'
                  }}
                    onClick={() => togglePassword(name === 'password' ? 'pass' : 'repeat')}
                  >
                    {value && <img width="20" height="20" src={require("../../../_assets/images/" +
                      ((name === 'password') ? (showPass ? "show.svg" : "hide.svg") :
                        (showRepeatPass ? "show.svg" : "hide.svg"))
                    )} alt="eyeIcon" />}
                  </p>
                )}
                {hasError && (
                  <FormFeedback>{errorMessage}</FormFeedback>
                )}
              </div>
            );
          }}
        </Field>
      </FormGroup>
    );
  };

  const changeUser = () => {
    const user = componentLoad === 'tenant' ? 'landlord' : 'tenant';
    props.history.push(`/signup/${user}`);
    window.location.reload();
  };

  return (
    <>
      <Helmet>
        <title>{`${componentLoad === 'tenant' ? "Tenant" : "Landlord"} Sign Up | Rent Panda`}</title>
      </Helmet>
      <AuthLayout title={`${componentLoad === 'tenant' ? "Tenant" : "Landlord"} sign up`} history={history}>
        <Formik
          initialValues={{
            firstName: "",
            lastName: "",
            email: "",
            phone: "",
            password: "",
            repeat_password: "",
            terms_and_services: false,
            honeypot: "",
          }}
          validationSchema={schema}
          onSubmit={async (values, actions) => {
            if (values.honeypot !== "") return;
            if (!termsAndService) {
              setModalTermsAndService(true);
              return;
            }
            try {
              const data = {
                first_name: values.firstName,
                last_name: values.lastName,
                email: values.email,
                phone_number: values.phone,
                password: values.password,
                repeat_password: values.repeat_password,
                user_type: componentLoad === 'tenant' ? 3 : 2,
              };
              const res = await axios.post(`${API_URL}/sign-up`, data);
              const eventCat = componentLoad === 'tenant' ? 'Tenant' : 'Landlord';
              captureEvent('Sign Up', eventCat, `${eventCat} Sign Up`);
              dispatch(signUp(res.data.data.email, res.data.data.user_id));
              history.push({ pathname: "/verification", state: { data: values.email, flow: 'signupflow' } });
            } catch (err) {
              const { errors } = err.response?.data || {};
              if (errors?.email) {
                setDuplicateEmail(errors.email[0]);
                actions.setFieldError("email", "error");
              }
              if (errors?.phone_number) {
                setDuplicatePhone(errors.phone_number[0]);
                actions.setFieldError("phone", "error");
              }
              if (errors?.password) {
                setPasswordError(errors.password[0]);
                actions.setFieldError("password", errors.password[0]);
              }
            }
          }}
        >
          {({ handleSubmit, isSubmitting }) => (
            <Form onSubmit={handleSubmit}>
              <Field type="text" name="honeypot" style={{ display: 'none' }} />
              {renderInput({ type: "text", id: "firstName", placeholder: "First Name", maxLength: 60 })}
              {renderInput({ type: "text", id: "lastName", placeholder: "Last Name", maxLength: 60 })}
              {renderInput({ type: "email", id: "email", placeholder: "Email Address" })}
              {renderInput({ type: "text", id: "phone", placeholder: "Phone Number", maxLength: 10 })}
              {renderInput({ type: "password", id: "password", placeholder: "Password" })}
              {renderInput({ type: "password", id: "repeat_password", placeholder: "Repeat Password" })}
              <CustomInput type="checkbox" id="terms_and_services" label={<label>Agree to <span className="terms-condition cursorPointer" onClick={() => setModalTermsAndService(true)}>terms of service</span></label>} checked={termsAndService} onChange={() => setTermsAndService(!termsAndService)} />
              <CustomInput type="checkbox" id="important_updates" label="Agree to receive important updates" checked={importantUpdate} onChange={() => setImportantUpdate(!importantUpdate)} />
              <Button type="submit" disabled={isSubmitting} className="btn btn-primary text-transform-none f-secondary-b mb-4" style={{ width: "100%" }}>
                Sign Up Now
              </Button>
              <p className="authentication-page-form-links" style={{ marginBottom: "35px" }}>
                Already have an account? <a href="/login" style={{ textDecoration: "underline" }}>Log in here</a>
              </p>
              <p className="authentication-page-form-links m-0">
                Sign up as <span style={{ cursor: "pointer", textDecoration: "underline" }} onClick={changeUser}>{componentLoad === 'tenant' ? "landlord" : "tenant"}</span>
              </p>
            </Form>
          )}
        </Formik>

        <ModalBox
          show={modalTermsAndService}
          onHide={() => setModalTermsAndService(false)}
          type="condition"
          actionbuttontitle="Agree To Terms Of Service"
          buttonaction={() => {
            setTermsAndService(true);
            setModalTermsAndService(false);
          }}
          backbuttontitle="Cancel"
          backaction={() => setModalTermsAndService(false)}
        >
          <TermsAndConditionContent />
        </ModalBox>
      </AuthLayout>
    </>
  );
};

export default withRouter(SignUp);

